// src/services/enduser/calculatorService.ts
import { API_BASE_URL } from '../../config/api';
import { TAX_YEAR_NOTICE } from '../../config/taxYearNotice';

// מודיע ל-AuthContext לרענן את סטטוס המנוי (יתרת ניסיון חינם) מיד אחרי חישוב סופי מוצלח,
// כדי שה-badge "נותרו X/5" יתעדכן בזמן אמת ולא רק בחזרה לדשבורד (verifySession).
const notifyFinalCalculationCompleted = (endpoint: string, result: any): void => {
  try {
    if (
      typeof window !== 'undefined' &&
      endpoint.includes('final_calculation') &&
      result && result.res === 'OK'
    ) {
      window.dispatchEvent(new CustomEvent('cartax:subscription-refresh'));
    }
  } catch {
    // לא חוסם את החזרת התוצאה אם הרענון נכשל
  }
};

// Types מעודכנים לפי ה-APIs החדשים
export interface CurrencyRate {
  currency_code: string;
  currency_name_hebrew: string;
  customs_rate: number;
  start_date: string;
  end_date: string;
}

export interface ExplanationData {
  title: string;
  text: string;
  order: number;
}

export interface GreenRatingGroup {
  id: number;
  group_number: number;
  group_display_name: string;
  co2_range_min: number;
  co2_range_max: number;
  discount_amount: number;
  is_active: boolean;
  sort_order: number;
}

export interface CalculatorData {
  currency_rates: CurrencyRate[];
  explanations: Record<string, ExplanationData>;
  green_rating_groups?: GreenRatingGroup[];
}

// עדכון CalculationInput לפי השדות הנכונים
export interface CalculationInput {
  manufacturing_date: string;
  vehicle_price_foreign: number;
  currency_type: string;
  exchange_rate: number;
  shipping_cost_foreign: number;
  insurance_cost_foreign: number;
  insurance_currency_type: string;
  insurance_exchange_rate: number;
  local_expenses_ils: number;
  has_origin_declaration: boolean;
  vehicle_weight_over_3500: boolean;
  green_rating_group: number;
  road_entry_date: string;
  target_consumer_price: number;
  shipping_time_months: number;
}

// Interface לקלט M1 חשמלי - ללא green_rating_group
export interface M1ElectricCalculationInput {
  manufacturing_date: string;
  vehicle_price_foreign: number;
  currency_type: string;
  exchange_rate: number;
  shipping_cost_foreign: number;
  insurance_cost_foreign: number;
  insurance_currency_type: string;
  insurance_exchange_rate: number;
  local_expenses_ils: number;
  has_origin_declaration: boolean;
  vehicle_weight_over_3500: boolean;
  road_entry_date: string; // במקום road_year + road_month
  shipping_time_months: number;
  target_consumer_price?: number; // Optional עבור Basic API
}


// תגובת API הראשון - חישוב בסיסי
export interface BasicCalculationResult {
  calculation_details: {
    input_data: CalculationInput;
    intermediate_calculations: {
      tax_value_ils: number;
      customs_fee: number;
      customs_rate: number;
      customs_rate_percent?: number;
      purchase_tax_base: number;
      green_discount: number;
      basic_purchase_tax_rate: number;
      needs_luxury_tax: boolean;
      shipping_time_months: number;
      year_transition_warning?: string;
    };
    luxury_tax_calculation?: {
      P0: number;
      P1: number;
      recommended_consumer_price: number;
      months_on_road: number;
      depreciation_rate: number;
      luxury_tax_rate: number;
    };
    final_results: {
      purchase_tax_rate: number;
      purchase_tax: number;
      vat_amount: number;
      total_cost: number;
      total_taxes?: number;
      luxury_tax_applicable?: boolean;
      effective_purchase_tax_rate?: number;
    };
  };
}
export interface N1ElectricCalculationInput {
  manufacturing_date: string;
  vehicle_price_foreign: number;
  currency_type: string;
  exchange_rate: number;
  shipping_cost_foreign: number;
  insurance_cost_foreign: number;
  insurance_currency_type: string;
  insurance_exchange_rate: number;
  local_expenses_ils: number;
  has_origin_declaration: boolean;
  vehicle_weight_over_3500: boolean;
  road_entry_date: string; // במקום road_year + road_month
  shipping_time_months: number;
  target_consumer_price?: number; // Optional עבור Basic API
}

// תגובת API השני - חישוב סופי
export interface FinalCalculationResult {
  calculation_summary: {
    vehicle_info: {
      manufacturing_date: string;
      vehicle_price_foreign: number;
      currency_type: string;
      exchange_rate: number;
      is_heavy_vehicle: boolean;
      green_rating_info: {
        group_number: number;
        group_display_name: string;
        co2_range_min?: number;
        co2_range_max?: number;
        discount_amount: number;
      };
    };
    base_calculations: {
      tax_value_ils: number;
      customs_fee: number;
      customs_rate_percent: number;
      purchase_tax_base: number;
      has_origin_declaration: boolean;
    };
    chosen_result: {
      title: string;
      luxury_tax_applicable: boolean;
      luxury_tax_rate: number;
      final_purchase_tax_rate: number;
      final_purchase_tax: number;
      vat_amount: number;
      total_cost: number;
      consumer_price_used: number;
      months_on_road: number;
      depreciation_rate: number;
      chosen_calculation_path?: number;
    };
    alternative_result?: {
      title: string;
      luxury_tax_applicable: boolean;
      luxury_tax_rate: number;
      final_purchase_tax_rate: number;
      final_purchase_tax: number;
      vat_amount: number;
      total_cost: number;
      consumer_price_used: number;
      months_on_road: number;
      depreciation_rate: number;
    };
    cost_breakdown: {
      tax_value: number;
      customs_fee: number;
      purchase_tax: number;
      vat: number;
      total: number;
    };
    comparison_info?: {
      price_difference: number;
      total_cost_difference: number;
    };
  };
  saved_calculation_id: number;
}

export interface ApiResponse<T = any> {
  res: 'OK' | 'NOK';
  msg: string;
  data?: T;
  error_code?: string;
}

class CalculatorService {
  private getSessionToken(): string | null {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'enduser_session_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  }

  private async makeRequest(endpoint: string, data: Record<string, any> = {}): Promise<ApiResponse> {
    const sessionToken = this.getSessionToken();

    if (!sessionToken) {
      throw new Error('No session token found');
    }

    const formData = new FormData();
    formData.append('session_token', sessionToken);

    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, value.toString());
      }
    });

    // console.log('🌐 Making request to:', `${API_BASE_URL}${endpoint}`);

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    notifyFinalCalculationCompleted(endpoint, result);
    return result;
  }

  /**
   * קבלת נתוני מחשבון מלאים - שערי מטבע + הסברים
   */
  async getCalculatorData(): Promise<ApiResponse<CalculatorData>> {
    try {
      // console.log('🔍 Fetching calculator data (rates + explanations)...');
      const response = await this.makeRequest('/Api/service/enduser/get_calculator_data');
      // console.log('📥 Calculator data response:', response);
      return response;
    } catch (error) {
      console.error('❌ Error fetching calculator data:', error);
      throw error;
    }
  }

  /**
   * API ראשון: חישוב בסיסי M1 - מחזיר P0, P1 ומחיר מומלץ
   */
  async getBasicCalculationM1(input: CalculationInput): Promise<ApiResponse<BasicCalculationResult>> {
    try {
      // console.log('🧮 Starting basic M1 calculation...');
      // console.log('📋 Input data:', input);

      const response = await this.makeRequest('/Api/service/enduser/get_basic_calculation_m1_private', {
        manufacturing_date: input.manufacturing_date,
        vehicle_price_foreign: input.vehicle_price_foreign,
        currency_type: input.currency_type,
        exchange_rate: input.exchange_rate,
        shipping_cost_foreign: input.shipping_cost_foreign,
        insurance_cost_foreign: input.insurance_cost_foreign,
        insurance_currency_type: input.insurance_currency_type,
        insurance_exchange_rate: input.insurance_exchange_rate,
        local_expenses_ils: input.local_expenses_ils,
        has_origin_declaration: input.has_origin_declaration,
        vehicle_weight_over_3500: input.vehicle_weight_over_3500,
        green_rating_group: input.green_rating_group,
        road_entry_date: input.road_entry_date,

        target_consumer_price: input.target_consumer_price,
        shipping_time_months: input.shipping_time_months
      });

      // console.log('📥 Basic calculation response:', response);
      return response;
    } catch (error) {
      console.error('❌ Error in basic M1 calculation:', error);
      throw error;
    }
  }

  /**
   * API שני: חישוב סופי M1 - מחזיר תוצאה סופית ושומר את החישוב
   */
  async getFinalCalculationM1(
    input: CalculationInput,
    chosenConsumerPrice: number
  ): Promise<ApiResponse<FinalCalculationResult>> {
    try {
      // console.log('🎯 Starting final M1 calculation...');
      // console.log('📋 Input data:', input);
      // console.log('💰 Chosen consumer price:', chosenConsumerPrice);

      const response = await this.makeRequest('/Api/service/enduser/get_final_calculation_m1_private', {
        manufacturing_date: input.manufacturing_date,
        vehicle_price_foreign: input.vehicle_price_foreign,
        currency_type: input.currency_type,
        exchange_rate: input.exchange_rate,
        shipping_cost_foreign: input.shipping_cost_foreign,
        insurance_cost_foreign: input.insurance_cost_foreign,
        insurance_currency_type: input.insurance_currency_type,
        insurance_exchange_rate: input.insurance_exchange_rate,
        local_expenses_ils: input.local_expenses_ils,
        has_origin_declaration: input.has_origin_declaration,
        vehicle_weight_over_3500: input.vehicle_weight_over_3500,
        green_rating_group: input.green_rating_group,
        road_entry_date: input.road_entry_date,
        target_consumer_price: input.target_consumer_price,
        chosen_consumer_price: chosenConsumerPrice,
        shipping_time_months: input.shipping_time_months
      });

      // console.log('📥 Final calculation response:', response);
      return response;
    } catch (error) {
      console.error('❌ Error in final M1 calculation:', error);
      throw error;
    }
  }


  /**
   * API ראשון: חישוב בסיסי M1 חשמלי - מחזיר P0, P1 ומחיר מומלץ
   * רק לרכבים מתחת ל-3500 ק"ג
   */
  async getBasicCalculationM1Electric(input: M1ElectricCalculationInput): Promise<ApiResponse<BasicCalculationResult>> {
    try {
      // console.log('🔋 Starting basic M1 electric calculation...');
      // console.log('📋 Input data:', input);

      const response = await this.makeRequest('/Api/service/enduser/get_basic_calculation_m1_electric_private', {
        manufacturing_date: input.manufacturing_date,
        vehicle_price_foreign: input.vehicle_price_foreign,
        currency_type: input.currency_type,
        exchange_rate: input.exchange_rate,
        shipping_cost_foreign: input.shipping_cost_foreign,
        insurance_cost_foreign: input.insurance_cost_foreign,
        insurance_currency_type: input.insurance_currency_type,
        insurance_exchange_rate: input.insurance_exchange_rate,
        local_expenses_ils: input.local_expenses_ils,
        has_origin_declaration: input.has_origin_declaration,
        vehicle_weight_over_3500: input.vehicle_weight_over_3500,
        road_entry_date: input.road_entry_date,
        shipping_time_months: input.shipping_time_months
      });

      // console.log('📥 Basic electric calculation response:', response);
      return response;
    } catch (error) {
      console.error('❌ Error in basic M1 electric calculation:', error);
      throw error;
    }
  }

  /**
   * API שני: חישוב סופי M1 חשמלי - מחזיר תוצאה סופית
   * רק לרכבים מתחת ל-3500 ק"ג
   */
  async getFinalCalculationM1Electric(
    input: M1ElectricCalculationInput,
    chosenConsumerPrice: number
  ): Promise<ApiResponse<FinalCalculationResult>> {
    try {
      // console.log('🎯 Starting final M1 electric calculation...');
      // console.log('📋 Input data:', input);
      // console.log('💰 Chosen consumer price:', chosenConsumerPrice);

      const response = await this.makeRequest('/Api/service/enduser/get_final_calculation_m1_electric_private', {
        manufacturing_date: input.manufacturing_date,
        vehicle_price_foreign: input.vehicle_price_foreign,
        currency_type: input.currency_type,
        exchange_rate: input.exchange_rate,
        shipping_cost_foreign: input.shipping_cost_foreign,
        insurance_cost_foreign: input.insurance_cost_foreign,
        insurance_currency_type: input.insurance_currency_type,
        insurance_exchange_rate: input.insurance_exchange_rate,
        local_expenses_ils: input.local_expenses_ils,
        has_origin_declaration: input.has_origin_declaration,
        road_entry_date: input.road_entry_date,
        target_consumer_price: input.target_consumer_price || 0,
        chosen_consumer_price: chosenConsumerPrice,
        shipping_time_months: input.shipping_time_months
      });

      // console.log('📥 Final electric calculation response:', response);
      return response;
    } catch (error) {
      console.error('❌ Error in final M1 electric calculation:', error);
      throw error;
    }
  }

  async getBasicCalculationN1Electric(input: N1ElectricCalculationInput): Promise<ApiResponse<BasicCalculationResult>> {
    try {
      // console.log('🔋 Starting basic N1 electric calculation...');
      // console.log('📋 Input data:', input);

      const response = await this.makeRequest('/Api/service/enduser/get_basic_calculation_n1_electric_private', {
        manufacturing_date: input.manufacturing_date,
        vehicle_price_foreign: input.vehicle_price_foreign,
        currency_type: input.currency_type,
        exchange_rate: input.exchange_rate,
        shipping_cost_foreign: input.shipping_cost_foreign,
        insurance_cost_foreign: input.insurance_cost_foreign,
        insurance_currency_type: input.insurance_currency_type,
        insurance_exchange_rate: input.insurance_exchange_rate,
        local_expenses_ils: input.local_expenses_ils,
        has_origin_declaration: input.has_origin_declaration,
        vehicle_weight_over_3500: input.vehicle_weight_over_3500,
        road_entry_date: input.road_entry_date,
        shipping_time_months: input.shipping_time_months
      });

      // console.log('📥 Basic N1 electric calculation response:', response);
      return response;
    } catch (error) {
      console.error('❌ Error in basic N1 electric calculation:', error);
      throw error;
    }
  }

  /**
   * API שני: חישוב סופי N1 חשמלי - מחזיר תוצאה סופית
   * רק לרכבים מתחת ל-3500 ק"ג
   */
  async getFinalCalculationN1Electric(
    input: N1ElectricCalculationInput,
    chosenConsumerPrice: number
  ): Promise<ApiResponse<FinalCalculationResult>> {
    try {
      // console.log('🎯 Starting final N1 electric calculation...');
      // console.log('📋 Input data:', input);
      // console.log('💰 Chosen consumer price:', chosenConsumerPrice);

      const response = await this.makeRequest('/Api/service/enduser/get_final_calculation_n1_electric_private', {
        manufacturing_date: input.manufacturing_date,
        vehicle_price_foreign: input.vehicle_price_foreign,
        currency_type: input.currency_type,
        exchange_rate: input.exchange_rate,
        shipping_cost_foreign: input.shipping_cost_foreign,
        insurance_cost_foreign: input.insurance_cost_foreign,
        insurance_currency_type: input.insurance_currency_type,
        insurance_exchange_rate: input.insurance_exchange_rate,
        local_expenses_ils: input.local_expenses_ils,
        has_origin_declaration: input.has_origin_declaration,
        road_entry_date: input.road_entry_date,
        target_consumer_price: input.target_consumer_price || 0,
        chosen_consumer_price: chosenConsumerPrice,
        shipping_time_months: input.shipping_time_months
      });

      // console.log('📥 Final N1 electric calculation response:', response);
      return response;
    } catch (error) {
      console.error('❌ Error in final N1 electric calculation:', error);
      throw error;
    }
  }


  async getBasicCalculationN1Tender(input: CalculationInput): Promise<ApiResponse<BasicCalculationResult>> {
    try {
      // console.log('🧮 Starting basic N1 tender calculation...');
      // console.log('📋 Input data:', input);

      const response = await this.makeRequest('/Api/service/enduser/get_basic_calculation_n1_tender', {
        manufacturing_date: input.manufacturing_date,
        vehicle_price_foreign: input.vehicle_price_foreign,
        currency_type: input.currency_type,
        exchange_rate: input.exchange_rate,
        shipping_cost_foreign: input.shipping_cost_foreign,
        insurance_cost_foreign: input.insurance_cost_foreign,
        insurance_currency_type: input.insurance_currency_type,
        insurance_exchange_rate: input.insurance_exchange_rate,
        local_expenses_ils: input.local_expenses_ils,
        has_origin_declaration: input.has_origin_declaration,
        vehicle_weight_over_3500: input.vehicle_weight_over_3500,
        green_rating_group: input.green_rating_group,
        road_entry_date: input.road_entry_date,
        target_consumer_price: input.target_consumer_price,
        shipping_time_months: input.shipping_time_months
      });

      // console.log('📥 Basic N1 tender calculation response:', response);
      return response;
    } catch (error) {
      console.error('❌ Error in basic N1 tender calculation:', error);
      throw error;
    }
  }

  /**
   * API שני: חישוב סופי N1 טנדר - מחזיר תוצאה סופית ושומר את החישוב
   */
  async getFinalCalculationN1Tender(
    input: CalculationInput,
    chosenConsumerPrice: number
  ): Promise<ApiResponse<FinalCalculationResult>> {
    try {
      // console.log('🎯 Starting final N1 tender calculation...');
      // console.log('📋 Input data:', input);
      // console.log('💰 Chosen consumer price:', chosenConsumerPrice);

      const response = await this.makeRequest('/Api/service/enduser/get_final_calculation_n1_tender', {
        manufacturing_date: input.manufacturing_date,
        vehicle_price_foreign: input.vehicle_price_foreign,
        currency_type: input.currency_type,
        exchange_rate: input.exchange_rate,
        shipping_cost_foreign: input.shipping_cost_foreign,
        insurance_cost_foreign: input.insurance_cost_foreign,
        insurance_currency_type: input.insurance_currency_type,
        insurance_exchange_rate: input.insurance_exchange_rate,
        local_expenses_ils: input.local_expenses_ils,
        has_origin_declaration: input.has_origin_declaration,
        vehicle_weight_over_3500: input.vehicle_weight_over_3500,
        green_rating_group: input.green_rating_group,
        road_entry_date: input.road_entry_date,
        target_consumer_price: input.target_consumer_price,
        chosen_consumer_price: chosenConsumerPrice,
        shipping_time_months: input.shipping_time_months
      });

      // console.log('📥 Final N1 tender calculation response:', response);
      return response;
    } catch (error) {
      console.error('❌ Error in final N1 tender calculation:', error);
      throw error;
    }
  }





  /**
   * עזרים לעיצוב
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  formatPercentage(rate: number): string {
    return `${rate.toFixed(2)}%`;
  }
}


// Types for Collector Vehicle Calculator
export interface CollectorVehicleCalculationRequest {
  manufacturing_date: string;
  vehicle_price_foreign: number;
  currency_type: string;
  exchange_rate: number;
  shipping_cost_foreign: number;
  insurance_cost_foreign: number;
  insurance_currency_type: string;
  insurance_exchange_rate: number;
  local_expenses_ils: number;
  has_origin_declaration: boolean;
  vehicle_weight_over_3500: boolean;
}

export interface CollectorVehicleCalculationResponse {
  input_data: CollectorVehicleCalculationRequest;
  intermediate_calculations: {
    vehicle_price_ils: number;
    shipping_cost_ils: number;
    insurance_cost_ils: number;
    total_before_customs: number;
    customs_value: number;
    origin_declaration_penalty: number;
  };
  final_results: {
    purchase_tax_base: number;
    purchase_tax: number;
    total_before_vat: number;
    vat: number;
    final_total: number;
  };
  calculation_info: {
    calculation_type: string;
    calculation_date: string;
    user_id: number;
    has_origin_declaration_discount: boolean;
    purchase_tax_rate: string;
    vat_rate: string;
    customs_penalty: string;
  };
  saved_calculation_id: number;
}
export interface TrailerCalculationRequest {
  manufacturing_date: string;
  vehicle_price_foreign: number;
  currency_type: string;
  exchange_rate: number;
  shipping_cost_foreign: number;
  insurance_cost_foreign: number;
  insurance_currency_type: string;
  insurance_exchange_rate: number;
  local_expenses_ils: number;
  vehicle_weight_over_3500: boolean;
}

export interface TrailerCalculationResponse {
  input_data: TrailerCalculationRequest;
  intermediate_calculations: {
    vehicle_price_ils: number;
    shipping_cost_ils: number;
    insurance_cost_ils: number;
    total_value: number;
  };
  final_results: {
    total_before_vat: number;
    vat: number;
    final_total: number;
  };
  calculation_info: {
    calculation_type: string;
    calculation_date: string;
    user_id: number;
    purchase_tax_applied: boolean;
    vat_rate: string;
    customs_applied: boolean;
  };
  saved_calculation_id: number;
}

// Types for Taxi Calculator
export interface TaxiCalculationRequest {
  vehicle_price_foreign: number;
  currency_type: string;
  exchange_rate: number;
  shipping_cost_foreign: number;
  insurance_cost_foreign: number;
  insurance_currency_type: string;
  insurance_exchange_rate: number;
  local_expenses_ils: number;
  engine_type: 'בנזין רגיל' | 'היברידי בנזין' | 'פלאג אין בנזין' | 'דיזל רגיל' | 'היברידי דיזל' | 'פלאג אין דיזל' | 'חשמלי';
  has_origin_declaration: boolean;
  green_rating_group?: number; // אופציונלי - רק אם לא חשמלי
  vehicle_weight_over_3500?: boolean;
}

export interface TaxiCalculationResponse {
  input_data: {
    vehicle_price_foreign: number;
    currency_type: string;
    exchange_rate: number;
    shipping_cost_foreign: number;
    insurance_cost_foreign: number;
    insurance_currency_type: string;
    insurance_exchange_rate: number;
    local_expenses_ils: number;
    engine_type: string;
    has_origin_declaration: boolean;
    green_rating_group: number | null;
  };
  intermediate_calculations: {
    vehicle_price_ils: number;
    shipping_cost_ils: number;
    insurance_cost_ils: number;
    total_value_for_customs: number;
    customs_fee: number;
    purchase_tax_base: number;
    purchase_tax_before_green: number;
    green_rating_info: {
      group_number: number;
      group_display_name: string;
      original_discount: number;
      applied_discount: number;
      auto_applied?: boolean;
      reason?: string;
      limited_by_purchase_tax?: boolean;
    } | null;
  };
  final_results: {
    purchase_tax_final: number;
    vat_base: number;
    vat: number;
    final_total: number;
  };
  calculation_info: {
    calculation_type: string;
    calculation_date: string;
    user_id: number;
    engine_type: string;
    customs_applied: boolean;
    customs_exemption_reason: string | null;
    purchase_tax_rate: string;
    vat_rate: string;
    green_rating_applied: boolean;
  };
  saved_calculation_id: number;
}

export interface MotorcycleCalculationRequest {
  manufacturing_date: string;
  vehicle_price_foreign: number;
  currency_type: string;
  exchange_rate: number;
  shipping_cost_foreign: number;
  insurance_cost_foreign: number;
  insurance_currency_type: string;
  insurance_exchange_rate: number;
  local_expenses_ils: number;
  has_origin_declaration: boolean;
  engine_capacity: number; // נפח מנוע בסמ"ק
}

export interface MotorcycleCalculationResponse {
  input_data: {
    manufacturing_date: string;
    vehicle_price_foreign: number;
    currency_type: string;
    exchange_rate: number;
    shipping_cost_foreign: number;
    insurance_cost_foreign: number;
    insurance_currency_type: string;
    insurance_exchange_rate: number;
    local_expenses_ils: number;
    has_origin_declaration: boolean;
    engine_capacity: number;
  };
  intermediate_calculations: {
    vehicle_price_ils: number;
    shipping_cost_ils: number;
    insurance_cost_ils: number;
    total_before_customs: number;
    customs_fee: number;
    customs_rate_percent: number;
    purchase_tax_base: number;
    engine_info: {
      capacity_cc: number;
      capacity_group: string;
      tax_rate_percent: number;
    };
    purchase_tax: number;
  };
  final_results: {
    vat_base: number;
    vat: number;
    final_total: number;
  };
  calculation_info: {
    calculation_type: string;
    calculation_date: string;
    user_id: number;
    customs_applied: boolean;
    customs_rate_percent: number;
    purchase_tax_rate_percent: number;
    engine_capacity_group: string;
    has_origin_declaration: boolean;
    vat_rate_percent: number;
  };
}
export const calculateTaxi = async (
  data: TaxiCalculationRequest
): Promise<ApiResponse<TaxiCalculationResponse>> => {
  // console.log('🚕 שליחת נתונים למחשבון מוניות:', data);

  // Get session token from cookies
  const getSessionToken = (): string | null => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'enduser_session_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  const sessionToken = getSessionToken();
  if (!sessionToken) {
    return {
      res: 'NOK',
      msg: 'לא נמצא token של המשתמש',
      error_code: 'NO_SESSION_TOKEN'
    };
  }

  const formData = new FormData();
  formData.append('session_token', sessionToken);

  // המרת כל השדות ל-FormData - שמירה על אותה תצורת שליחה
  formData.append('vehicle_price_foreign', data.vehicle_price_foreign.toString());
  formData.append('currency_type', data.currency_type);
  formData.append('exchange_rate', data.exchange_rate.toString());
  formData.append('shipping_cost_foreign', data.shipping_cost_foreign.toString());
  formData.append('insurance_cost_foreign', data.insurance_cost_foreign.toString());
  formData.append('insurance_currency_type', data.insurance_currency_type);
  formData.append('insurance_exchange_rate', data.insurance_exchange_rate.toString());
  formData.append('local_expenses_ils', data.local_expenses_ils.toString());
  formData.append('engine_type', data.engine_type);
  formData.append('has_origin_declaration', data.has_origin_declaration ? '1' : '0');

  // שדה חדש - משקל רכב
  if (data.vehicle_weight_over_3500 !== undefined) {
    formData.append('vehicle_weight_over_3500', data.vehicle_weight_over_3500 ? '1' : '0');
  }

  // ציון ירוק -  חשוב למנוע חשמלי
  if (data.engine_type === 'חשמלי') {
    // מנוע חשמלי תמיד מקבל קבוצה 1 אוטומטית
    formData.append('green_rating_group', '1');
  } else if (data.green_rating_group) {
    // רכבים לא חשמליים - ציון ירוק ידני
    formData.append('green_rating_group', data.green_rating_group.toString());
  }

  try {
    // console.log('🌐 Making request to:', `${API_BASE_URL}/Api/service/enduser/get_taxi_calculation`);

    const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_taxi_calculation`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // console.log('📥 תשובה מהשרת למחשבון מוניות:', result);

    return result;
  } catch (error) {
    console.error('❌ שגיאה במחשבון מוניות:', error);
    return {
      res: 'NOK',
      msg: 'שגיאה בתקשורת עם השרת',
      error_code: 'NETWORK_ERROR'
    };
  }
};


export const getCollectorVehicleCalculation = async (
  data: CollectorVehicleCalculationRequest
): Promise<ApiResponse<CollectorVehicleCalculationResponse>> => {
  // console.log('🚗 שליחת נתונים למחשבון רכבי אספנות:', data);

  // Get session token from cookies
  const getSessionToken = (): string | null => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'enduser_session_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  const sessionToken = getSessionToken();
  if (!sessionToken) {
    return {
      res: 'NOK',
      msg: 'לא נמצא token של המשתמש',
      error_code: 'NO_SESSION_TOKEN'
    };
  }

  const formData = new FormData();
  formData.append('session_token', sessionToken);

  // המרת כל השדות ל-FormData
  formData.append('manufacturing_date', data.manufacturing_date);
  formData.append('vehicle_price_foreign', data.vehicle_price_foreign.toString());
  formData.append('currency_type', data.currency_type);
  formData.append('exchange_rate', data.exchange_rate.toString());
  formData.append('shipping_cost_foreign', data.shipping_cost_foreign.toString());
  formData.append('insurance_cost_foreign', data.insurance_cost_foreign.toString());
  formData.append('insurance_currency_type', data.insurance_currency_type);
  formData.append('insurance_exchange_rate', data.insurance_exchange_rate.toString());
  formData.append('local_expenses_ils', data.local_expenses_ils.toString());
  formData.append('has_origin_declaration', data.has_origin_declaration ? '1' : '0');
  formData.append('vehicle_weight_over_3500', data.vehicle_weight_over_3500 ? '1' : '0');

  try {
    // console.log('🌐 Making request to:', `${API_BASE_URL}/Api/service/enduser/get_collector_vehicle_calculation`);

    const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_collector_vehicle_calculation`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // console.log('📥 תשובה מהשרת למחשבון רכבי אספנות:', result);

    return result;
  } catch (error) {
    console.error('❌ שגיאה במחשבון רכבי אספנות:', error);
    return {
      res: 'NOK',
      msg: 'שגיאה בתקשורת עם השרת',
      error_code: 'NETWORK_ERROR'
    };
  }
};
export const calculateTrailer = async (
  data: TrailerCalculationRequest
): Promise<ApiResponse<TrailerCalculationResponse>> => {
  // console.log('🚛 שליחת נתונים למחשבון נגררים:', data);

  // Get session token from cookies
  const getSessionToken = (): string | null => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'enduser_session_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  const sessionToken = getSessionToken();
  if (!sessionToken) {
    return {
      res: 'NOK',
      msg: 'לא נמצא token של המשתמש',
      error_code: 'NO_SESSION_TOKEN'
    };
  }

  const formData = new FormData();
  formData.append('session_token', sessionToken);

  // המרת כל השדות ל-FormData
  formData.append('manufacturing_date', data.manufacturing_date);
  formData.append('vehicle_price_foreign', data.vehicle_price_foreign.toString());
  formData.append('currency_type', data.currency_type);
  formData.append('exchange_rate', data.exchange_rate.toString());
  formData.append('shipping_cost_foreign', data.shipping_cost_foreign.toString());
  formData.append('insurance_cost_foreign', data.insurance_cost_foreign.toString());
  formData.append('insurance_currency_type', data.insurance_currency_type);
  formData.append('insurance_exchange_rate', data.insurance_exchange_rate.toString());
  formData.append('local_expenses_ils', data.local_expenses_ils.toString());
  formData.append('vehicle_weight_over_3500', data.vehicle_weight_over_3500 ? '1' : '0');
  // אין has_origin_declaration - אין מכס על נגררים

  try {
    // console.log('🌐 Making request to:', `${API_BASE_URL}/Api/service/enduser/get_trailer_calculation`);

    const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_trailer_calculation`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // console.log('📥 תשובה מהשרת למחשבון נגררים:', result);

    return result;
  } catch (error) {
    console.error('❌ שגיאה במחשבון נגררים:', error);
    return {
      res: 'NOK',
      msg: 'שגיאה בתקשורת עם השרת',
      error_code: 'NETWORK_ERROR'
    };
  }
};

// Types for Jet Ski Calculator (אופנוע ים)
export interface JetSkiCalculationRequest {
  manufacturing_date: string;
  vehicle_price_foreign: number;
  currency_type: string;
  exchange_rate: number;
  shipping_cost_foreign: number;
  insurance_cost_foreign: number;
  insurance_currency_type: string;
  insurance_exchange_rate: number;
  local_expenses_ils: number;
  has_origin_declaration: boolean; // רק לתצוגה - לא משפיע על חישוב
}

export interface JetSkiCalculationResponse {
  input_data: {
    manufacturing_date: string;
    vehicle_price_foreign: number;
    currency_type: string;
    exchange_rate: number;
    shipping_cost_foreign: number;
    insurance_cost_foreign: number;
    insurance_currency_type: string;
    insurance_exchange_rate: number;
    local_expenses_ils: number;
    has_origin_declaration: boolean;
  };
  intermediate_calculations: {
    vehicle_price_ils: number;
    shipping_cost_ils: number;
    insurance_cost_ils: number;
    total_value_for_customs: number;
    customs_fee: number; // תמיד 0
    purchase_tax_base: number;
    purchase_tax: number; // 20%
  };
  final_results: {
    vat_base: number;
    vat: number; // 18%
    final_total: number;
  };
  calculation_info: {
    calculation_type: string;
    calculation_date: string;
    user_id: number;
    customs_applied: boolean; // תמיד false
    customs_exemption_reason: string;
    purchase_tax_rate: string; // '20%'
    vat_rate: string; // '18%'
    has_origin_declaration: boolean;
  };
  saved_calculation_id: number;
}

/**
 * Interface לבקשת חישוב אופנוע מסחרי
 * כולל נתוני אבזור בטיחותי
 */
export interface MotorcycleCommercialCalculationRequest {
  manufacturing_date: string;
  vehicle_price_foreign: number;
  currency_type: string;
  exchange_rate: number;
  shipping_cost_foreign: number;
  insurance_cost_foreign: number;
  insurance_currency_type: string;
  insurance_exchange_rate: number;
  local_expenses_ils: number;
  has_origin_declaration: boolean;
  engine_capacity: string | number; // יכול להיות מספר או "electric"
  safety_features?: {
    abs: boolean;
    stability_control: boolean;
    integrated_braking: boolean;
    steering_damper: boolean;
    traction_control: boolean;
    three_wheeled: boolean;
  };
}
/**
 * Interface לבקשת חישוב אופנועי ים מסחרי
 * זהה לייבוא אישי - החישוב זהה לחלוטין
 */
export interface JetSkiCommercialCalculationRequest {
  manufacturing_date: string;
  vehicle_price_foreign: number;
  currency_type: string;
  exchange_rate: number;
  shipping_cost_foreign: number;
  insurance_cost_foreign: number;
  insurance_currency_type: string;
  insurance_exchange_rate: number;
  local_expenses_ils: number;
  has_origin_declaration: boolean; // רק לתצוגה - לא משפיע על חישוב
}

/**
 * Interface לתשובת חישוב אופנועי ים מסחרי
 * זהה לייבוא אישי למעט calculation_type
 */
export interface JetSkiCommercialCalculationResponse {
  input_data: {
    manufacturing_date: string;
    vehicle_price_foreign: number;
    currency_type: string;
    exchange_rate: number;
    shipping_cost_foreign: number;
    insurance_cost_foreign: number;
    insurance_currency_type: string;
    insurance_exchange_rate: number;
    local_expenses_ils: number;
    has_origin_declaration: boolean;
  };
  intermediate_calculations: {
    vehicle_price_ils: number;
    shipping_cost_ils: number;
    insurance_cost_ils: number;
    total_value_for_customs: number;
    customs_fee: number; // תמיד 0
    purchase_tax_base: number;
    purchase_tax: number; // 20%
  };
  final_results: {
    vat_base: number;
    vat: number; // 18%
    final_total: number;
  };
  calculation_info: {
    calculation_type: string; // 'jet_ski_commercial'
    calculation_date: string;
    user_id: number;
    customs_applied: boolean; // תמיד false
    customs_exemption_reason: string;
    purchase_tax_rate: string; // '20%'
    vat_rate: string; // '18%'
    has_origin_declaration: boolean;
  };
  saved_calculation_id: number;
}
/**
 * Interface לתגובת חישוב אופנוע מסחרי
 * כולל מידע על הנחות אבזור בטיחותי
 */
export interface MotorcycleCommercialCalculationResponse {
  input_data: {
    manufacturing_date: string;
    vehicle_price_foreign: number;
    currency_type: string;
    exchange_rate: number;
    shipping_cost_foreign: number;
    insurance_cost_foreign: number;
    insurance_currency_type: string;
    insurance_exchange_rate: number;
    local_expenses_ils: number;
    has_origin_declaration: boolean;
    engine_capacity: string;
    is_electric: boolean;
    safety_features: {
      abs: boolean;
      stability_control: boolean;
      integrated_braking: boolean;
      steering_damper: boolean;
      traction_control: boolean;
      three_wheeled: boolean;
    };
  };
  intermediate_calculations: {
    vehicle_price_ils: number;
    shipping_cost_ils: number;
    insurance_cost_ils: number;
    base_value_before_taxes: number;
    total_before_customs: number;
    customs_fee: number;
    customs_rate_percent: number;
    purchase_tax_base: number;
    engine_info: {
      capacity_cc: number;
      capacity_group: string;
      tax_rate_percent: number;
      is_electric: boolean;
    };
    purchase_tax_before_discount: number;
    purchase_tax: number;
    total_taxes: number;
    tax_percentage: number;
  };
  safety_equipment: {
    features: {
      abs: boolean;
      stability_control: boolean;
      integrated_braking: boolean;
      steering_damper: boolean;
      traction_control: boolean;
      three_wheeled: boolean;
    };
    discount_amount: number;
    is_eligible: boolean;
    applied_to_purchase_tax: boolean;
    max_discount_allowed: number;
  };
  final_results: {
    vat_base: number;
    vat: number;
    final_total: number;
    final_total_without_discount: number;
    total_savings_from_safety: number;
  };
  calculation_info: {
    calculation_type: string;
    calculation_date: string;
    user_id: number;
    customs_applied: boolean;
    customs_rate_percent: number;
    purchase_tax_rate_percent: number;
    engine_capacity_group: string;
    has_origin_declaration: boolean;
    vat_rate_percent: number;
    is_electric_motorcycle: boolean;
    safety_discount_applied: boolean;
  };
}


export interface TaxiCommercialCalculationRequest {
  vehicle_price_foreign: number;
  currency_type: string;
  exchange_rate: number;
  shipping_cost_foreign: number;
  insurance_cost_foreign: number;
  insurance_currency_type: string;
  insurance_exchange_rate: number;
  local_expenses_ils: number;
  engine_type: 'בנזין רגיל' | 'היברידי בנזין' | 'פלאג אין בנזין' | 'דיזל רגיל' | 'היברידי דיזל' | 'פלאג אין דיזל' | 'חשמלי';
  has_origin_declaration: boolean;
  green_rating_group?: number;
  vehicle_weight_over_3500: boolean;
  safety_features?: {
    airbagsCount: number;
    systems: {
      blindSpotDetection: boolean;
      blindSpotCollisionPrevention: boolean;
      reverseBraking: boolean;
      driverDistraction: boolean;
      childAlert: boolean;
      doorOpenWarning: boolean;
      adaptiveLighting: boolean;
      junctionEmergencyBraking: boolean;
    };
  };
}

/**
 * Interface לתשובת חישוב מונית מסחרי
 * כולל מידע על אבזור בטיחותי והנחות
 */
export interface TaxiCommercialCalculationResponse {
  input_data: {
    [x: string]: any;
    vehicle_price_foreign: number;
    currency_type: string;
    exchange_rate: number;
    shipping_cost_foreign: number;
    insurance_cost_foreign: number;
    insurance_currency_type: string;
    insurance_exchange_rate: number;
    local_expenses_ils: number;
    engine_type: string;
    has_origin_declaration: boolean;
    green_rating_group: number | null;
    vehicle_weight: number;
    is_heavy_vehicle: boolean;
    safety_features: {
      airbagsCount: number;
      systems: {
        blindSpotDetection: boolean;
        blindSpotCollisionPrevention: boolean;
        reverseBraking: boolean;
        driverDistraction: boolean;
        childAlert: boolean;
        doorOpenWarning: boolean;
        adaptiveLighting: boolean;
        junctionEmergencyBraking: boolean;
      };
    } | null;
  };
  intermediate_calculations: {
    vehicle_price_ils: number;
    shipping_cost_ils: number;
    insurance_cost_ils: number;
    total_value_for_customs: number;
    customs_fee: number;
    customs_rate_percent: number;
    purchase_tax_base: number;
    purchase_tax_before_adjustments: number;
    green_rating_info: {
      group_number: number;
      group_display_name: string;
      raw_amount: number;
      applied_adjustment: number;
      is_discount: boolean;
      auto_applied?: boolean;
      reason?: string;
      limited_by_purchase_tax?: boolean;
    } | null;
    effective_purchase_tax_rate: number;
  };
  safety_equipment: {
    features: {
      airbagsCount: number;
      systems: {
        blindSpotDetection: boolean;
        blindSpotCollisionPrevention: boolean;
        reverseBraking: boolean;
        driverDistraction: boolean;
        childAlert: boolean;
        doorOpenWarning: boolean;
        adaptiveLighting: boolean;
        junctionEmergencyBraking: boolean;
      };
    };
    safety_score: number;
    safety_level: number;
    discount_amount: number;
    is_eligible: boolean;
    max_discount_allowed: number;
  } | null;
  final_results: {
    purchase_tax_final: number;
    vat_base: number;
    vat: number;
    final_total: number;
    total_taxes: number;
    tax_percentage: number;
  };
  calculation_info: {
    calculation_type: string;
    calculation_date: string;
    user_id: number;
    engine_type: string;
    customs_applied: boolean;
    customs_exemption_reason: string | null;
    customs_rate_percent: number;
    purchase_tax_rate: string;
    purchase_tax_rate_percent: number;
    vat_rate: string;
    vat_rate_percent: number;
    green_rating_applied: boolean;
    safety_discount_applied: boolean;
    is_heavy_vehicle: boolean;
  };
  saved_calculation_id: number;
}


/**
 * חישוב ייבוא מסחרי של אופנועים
 * כולל חישוב אבזור בטיחותי בצד השרת
 */
export const calculateMotorcycleCommercial = async (
  data: MotorcycleCommercialCalculationRequest
): Promise<ApiResponse<MotorcycleCommercialCalculationResponse>> => {
  // console.log('🏍️🏢 שליחת נתונים למחשבון אופנועים מסחרי:', data);

  // Get session token from cookies
  const getSessionToken = (): string | null => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'enduser_session_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  const sessionToken = getSessionToken();
  if (!sessionToken) {
    return {
      res: 'NOK',
      msg: 'לא נמצא token של המשתמש',
      error_code: 'NO_SESSION_TOKEN'
    };
  }

  const formData = new FormData();
  formData.append('session_token', sessionToken);

  // המרת כל השדות ל-FormData
  formData.append('manufacturing_date', data.manufacturing_date);
  formData.append('vehicle_price_foreign', data.vehicle_price_foreign.toString());
  formData.append('currency_type', data.currency_type);
  formData.append('exchange_rate', data.exchange_rate.toString());
  formData.append('shipping_cost_foreign', data.shipping_cost_foreign.toString());
  formData.append('insurance_cost_foreign', data.insurance_cost_foreign.toString());
  formData.append('insurance_currency_type', data.insurance_currency_type);
  formData.append('insurance_exchange_rate', data.insurance_exchange_rate.toString());
  formData.append('local_expenses_ils', data.local_expenses_ils.toString());
  formData.append('has_origin_declaration', data.has_origin_declaration ? '1' : '0');
  formData.append('engine_capacity', data.engine_capacity.toString());

  // שליחת נתוני אבזור בטיחותי כ-JSON
  if (data.safety_features) {
    formData.append('safety_features', JSON.stringify(data.safety_features));
  }

  try {
    // console.log('🌐 Making request to:', `${API_BASE_URL}/Api/service/enduser/get_motorcycle_commercial_calculation`);

    const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_motorcycle_commercial_calculation`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // console.log('📥 תשובה מהשרת למחשבון אופנועים מסחרי:', result);

    return result;
  } catch (error) {
    console.error('❌ שגיאה במחשבון אופנועים מסחרי:', error);
    return {
      res: 'NOK',
      msg: 'שגיאה בתקשורת עם השרת',
      error_code: 'NETWORK_ERROR'
    };
  }
};
export const calculateJetSki = async (
  data: JetSkiCalculationRequest
): Promise<ApiResponse<JetSkiCalculationResponse>> => {
  // console.log('🚤 שליחת נתונים למחשבון אופנוע ים:', data);

  // Get session token from cookies
  const getSessionToken = (): string | null => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'enduser_session_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  const sessionToken = getSessionToken();
  if (!sessionToken) {
    return {
      res: 'NOK',
      msg: 'לא נמצא token של המשתמש',
      error_code: 'NO_SESSION_TOKEN'
    };
  }

  const formData = new FormData();
  formData.append('session_token', sessionToken);

  // המרת כל השדות ל-FormData
  formData.append('manufacturing_date', data.manufacturing_date);
  formData.append('vehicle_price_foreign', data.vehicle_price_foreign.toString());
  formData.append('currency_type', data.currency_type);
  formData.append('exchange_rate', data.exchange_rate.toString());
  formData.append('shipping_cost_foreign', data.shipping_cost_foreign.toString());
  formData.append('insurance_cost_foreign', data.insurance_cost_foreign.toString());
  formData.append('insurance_currency_type', data.insurance_currency_type || data.currency_type);
  formData.append('insurance_exchange_rate', data.insurance_exchange_rate.toString());
  formData.append('local_expenses_ils', data.local_expenses_ils.toString());
  formData.append('has_origin_declaration', data.has_origin_declaration ? '1' : '0');

  try {
    const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_jet_ski_calculation`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result: ApiResponse<JetSkiCalculationResponse> = await response.json();
    // console.log('🚤 תגובת השרת מחשבון אופנוע ים:', result);

    return result;
  } catch (error) {
    console.error('❌ שגיאה בחישוב אופנוע ים:', error);
    return {
      res: 'NOK',
      msg: 'שגיאה בתקשורת עם השרת',
      error_code: 'NETWORK_ERROR'
    };
  }
};


export interface SaveCalculationRequest {
  calculation_type: string;
  input_data: any;
  result_data: any;
  notes?: string;
}

// Interface לתגובת שמירה
export interface SaveCalculationResponse {
  saved_calculation_id: number;
  calculation_type: string;
  user_notes: string;
  final_notes: string;
  saved_at: string;
}

// פונקציה לשמירת חישוב עם הערות
export const saveCalculation = async (
  data: SaveCalculationRequest
): Promise<ApiResponse<SaveCalculationResponse>> => {

  // Get session token from cookies
  const getSessionToken = (): string | null => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'enduser_session_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  const sessionToken = getSessionToken();
  if (!sessionToken) {
    return {
      res: 'NOK',
      msg: 'לא נמצא token של המשתמש',
      error_code: 'NO_SESSION_TOKEN'
    };
  }

  const formData = new FormData();
  formData.append('session_token', sessionToken);
  formData.append('calculation_type', data.calculation_type);
  formData.append('input_data', JSON.stringify(data.input_data));
  // snapshot של הערת שנת המס: חישוב שנשמר ב-2026 ימשיך להציג "2026" בהיסטוריה
  // גם אחרי שהנוסח הנוכחי ישתנה (ינואר 2027). נקודת שמירה יחידה לכל 16 המחשבונים.
  formData.append(
    'result_data',
    JSON.stringify({ ...(data.result_data || {}), tax_year_notice: TAX_YEAR_NOTICE })
  );

  // הוספת הערות רק אם קיימות
  if (data.notes && data.notes.trim()) {
    formData.append('notes', data.notes.trim());
  }

  try {
    // console.log('🌐 Making request to:', `${API_BASE_URL}/Api/service/enduser/save_calculation`);

    const response = await fetch(`${API_BASE_URL}/Api/service/enduser/save_calculation`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // console.log('📥 תשובה מהשרת לשמירת חישוב:', result);

    return result;
  } catch (error) {
    console.error('❌ שגיאה בשמירת חישוב:', error);
    return {
      res: 'NOK',
      msg: 'שגיאה בתקשורת עם השרת',
      error_code: 'NETWORK_ERROR'
    };
  }
};
/**
 * חישוב מיסים לאופנוע ים - ייבוא מסחרי
 * זהה לחלוטין לייבוא אישי למעט calculation_type
 */
export const calculateJetSkiCommercial = async (
  data: JetSkiCommercialCalculationRequest
): Promise<ApiResponse<JetSkiCommercialCalculationResponse>> => {
  // console.log('🚤 שליחת נתונים למחשבון אופנוע ים מסחרי:', data);

  // Get session token from cookies
  const getSessionToken = (): string | null => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'enduser_session_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  const sessionToken = getSessionToken();
  if (!sessionToken) {
    return {
      res: 'NOK',
      msg: 'לא נמצא token של המשתמש',
      error_code: 'NO_SESSION_TOKEN'
    };
  }

  const formData = new FormData();
  formData.append('session_token', sessionToken);

  // המרת כל השדות ל-FormData
  formData.append('manufacturing_date', data.manufacturing_date);
  formData.append('vehicle_price_foreign', data.vehicle_price_foreign.toString());
  formData.append('currency_type', data.currency_type);
  formData.append('exchange_rate', data.exchange_rate.toString());
  formData.append('shipping_cost_foreign', data.shipping_cost_foreign.toString());
  formData.append('insurance_cost_foreign', data.insurance_cost_foreign.toString());
  formData.append('insurance_currency_type', data.insurance_currency_type || data.currency_type);
  formData.append('insurance_exchange_rate', data.insurance_exchange_rate.toString());
  formData.append('local_expenses_ils', data.local_expenses_ils.toString());
  formData.append('has_origin_declaration', data.has_origin_declaration ? '1' : '0');

  try {
    const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_jet_ski_commercial_calculation`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result: ApiResponse<JetSkiCommercialCalculationResponse> = await response.json();
    // console.log('🚤 תגובת השרת מחשבון אופנוע ים מסחרי:', result);

    return result;
  } catch (error) {
    console.error('❌ שגיאה בחישוב אופנוע ים מסחרי:', error);
    return {
      res: 'NOK',
      msg: 'שגיאה בתקשורת עם השרת',
      error_code: 'NETWORK_ERROR'
    };
  }
};

/**
 * חישוב ייבוא מסחרי של מוניות
 * כולל חישוב אבזור בטיחותי M1 בצד השרת
 * לוגיקה מותנית: > 3500 ק"ג = מס קניה 0%, ≤ 3500 ק"ג = מס קניה 8% + אבזור
 */
export const calculateTaxiCommercial = async (
  data: TaxiCommercialCalculationRequest
): Promise<ApiResponse<TaxiCommercialCalculationResponse>> => {
  // console.log('🚕🏢 שליחת נתונים למחשבון מוניות מסחרי:', data);

  // Get session token from cookies
  const getSessionToken = (): string | null => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'enduser_session_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  const sessionToken = getSessionToken();
  if (!sessionToken) {
    return {
      res: 'NOK',
      msg: 'לא נמצא token של המשתמש',
      error_code: 'NO_SESSION_TOKEN'
    };
  }

  const formData = new FormData();
  formData.append('session_token', sessionToken);

  // המרת כל השדות ל-FormData
  formData.append('vehicle_price_foreign', data.vehicle_price_foreign.toString());
  formData.append('currency_type', data.currency_type);
  formData.append('exchange_rate', data.exchange_rate.toString());
  formData.append('shipping_cost_foreign', data.shipping_cost_foreign.toString());
  formData.append('insurance_cost_foreign', data.insurance_cost_foreign.toString());
  formData.append('insurance_currency_type', data.insurance_currency_type);
  formData.append('insurance_exchange_rate', data.insurance_exchange_rate.toString());
  formData.append('local_expenses_ils', data.local_expenses_ils.toString());
  formData.append('engine_type', data.engine_type);
  formData.append('has_origin_declaration', data.has_origin_declaration ? '1' : '0');
  formData.append('vehicle_weight_over_3500', data.vehicle_weight_over_3500 ? '1' : '0');


  // ציון ירוק - חשוב למנוע חשמלי
  if (data.engine_type === 'חשמלי') {
    // מנוע חשמלי תמיד מקבל קבוצה 1 אוטומטית
    formData.append('green_rating_group', '1');
  } else if (data.green_rating_group) {
    // מנועים אחרים - ציון ירוק ידני
    formData.append('green_rating_group', data.green_rating_group.toString());
  }

  // שליחת נתוני אבזור בטיחותי כ-JSON (רק אם הרכב ≤ 3500 ק"ג)
  if (!data.vehicle_weight_over_3500 && data.safety_features) {
    formData.append('safety_features', JSON.stringify(data.safety_features));
  }

  try {
    // console.log('🌐 Making request to:', `${API_BASE_URL}/Api/service/enduser/get_taxi_commercial_calculation`);

    const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_taxi_commercial_calculation`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // console.log('📥 תשובה מהשרת למחשבון מוניות מסחרי:', result);

    return result;
  } catch (error) {
    console.error('❌ שגיאה במחשבון מוניות מסחרי:', error);
    return {
      res: 'NOK',
      msg: 'שגיאה בתקשורת עם השרת',
      error_code: 'NETWORK_ERROR'
    };
  }
};

export const calculateMotorcycle = async (
  data: MotorcycleCalculationRequest
): Promise<ApiResponse<MotorcycleCalculationResponse>> => {
  // console.log('🏍️ שליחת נתונים למחשבון אופנועים:', data);

  // Get session token from cookies
  const getSessionToken = (): string | null => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'enduser_session_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  const sessionToken = getSessionToken();
  if (!sessionToken) {
    return {
      res: 'NOK',
      msg: 'לא נמצא token של המשתמש',
      error_code: 'NO_SESSION_TOKEN'
    };
  }

  const formData = new FormData();
  formData.append('session_token', sessionToken);

  // המרת כל השדות ל-FormData
  formData.append('manufacturing_date', data.manufacturing_date);
  formData.append('vehicle_price_foreign', data.vehicle_price_foreign.toString());
  formData.append('currency_type', data.currency_type);
  formData.append('exchange_rate', data.exchange_rate.toString());
  formData.append('shipping_cost_foreign', data.shipping_cost_foreign.toString());
  formData.append('insurance_cost_foreign', data.insurance_cost_foreign.toString());
  formData.append('insurance_currency_type', data.insurance_currency_type);
  formData.append('insurance_exchange_rate', data.insurance_exchange_rate.toString());
  formData.append('local_expenses_ils', data.local_expenses_ils.toString());
  formData.append('has_origin_declaration', data.has_origin_declaration ? '1' : '0');
  formData.append('engine_capacity', data.engine_capacity.toString());

  try {
    // console.log('🌐 Making request to:', `${API_BASE_URL}/Api/service/enduser/get_motorcycle_calculation`);

    const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_motorcycle_calculation`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // console.log('📥 תשובה מהשרת למחשבון אופנועים:', result);

    return result;
  } catch (error) {
    console.error('❌ שגיאה במחשבון אופנועים:', error);
    return {
      res: 'NOK',
      msg: 'שגיאה בתקשורת עם השרת',
      error_code: 'NETWORK_ERROR'
    };
  }
};



/**
 * חישוב M1 בנזין/דיזל/היברידי - ייבוא מסחרי (שלב 1)
 * 3 מסלולים לפי משקל:
 * 1. מעל 4.5 טון: רק מכס + מע"מ
 * 2. 3.5-4.5 טון: מס קניה 72%
 * 3. מתחת ל-3.5 טון: מס קניה 83% + ציון ירוק + אבזור M1
 */
const calculateM1Commercial = async (
  data: {
    manufacturing_date: string;
    vehicle_price_foreign: number;
    currency_type: string;
    exchange_rate: number;
    shipping_cost_foreign: number;
    insurance_cost_foreign: number;
    insurance_currency_type: string;
    insurance_exchange_rate: number;
    local_expenses_ils: number;
    has_origin_declaration: boolean;
    green_rating_group?: number;
    vehicle_weight_category: 'under_3500' | 'between_3500_4500' | 'over_4500';
    airbags_count?: number; // מספר כריות למסלול 2 (3.5-4.5 טון)
    safety_features?: {
      airbagsCount: number;
      systems: {
        blindSpotDetection: boolean;           // זיהוי כלי רכב ב"שטח מת"
        blindSpotCollisionPrevention: boolean; // אקטיבית למניעת התנגשות צד
        reverseBraking: boolean;               // בלימה אוטומטית לאחור
        driverDistraction: boolean;            // התרעה היסח דעת הנהג
        childAlert: boolean;                   // התרעה ילדים ברכב
        doorOpenWarning: boolean;              // אזהרת פתיחת דלת
        adaptiveLighting: boolean;             // תאורה אדפטיבית
        junctionEmergencyBraking: boolean;     // בלימת חירום בצמתים
      };
    };
  }
): Promise<ApiResponse<any>> => {
  // console.log('🚗🏢 שליחת נתונים למחשבון M1 מסחרי (שלב 1):', data);

  // Get session token from cookies
  const getSessionToken = (): string | null => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'enduser_session_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  const sessionToken = getSessionToken();
  if (!sessionToken) {
    return {
      res: 'NOK',
      msg: 'לא נמצא token של המשתמש',
      error_code: 'NO_SESSION_TOKEN'
    };
  }

  const formData = new FormData();
  formData.append('session_token', sessionToken);

  // המרת כל השדות ל-FormData
  formData.append('manufacturing_date', data.manufacturing_date);
  formData.append('vehicle_price_foreign', data.vehicle_price_foreign.toString())
  formData.append('currency_type', data.currency_type);
  formData.append('exchange_rate', data.exchange_rate.toString());
  formData.append('shipping_cost_foreign', data.shipping_cost_foreign.toString());
  formData.append('insurance_cost_foreign', data.insurance_cost_foreign.toString());
  formData.append('insurance_currency_type', data.insurance_currency_type || data.currency_type);
  formData.append('insurance_exchange_rate', data.insurance_exchange_rate.toString());
  formData.append('local_expenses_ils', data.local_expenses_ils.toString());
  formData.append('has_origin_declaration', data.has_origin_declaration ? '1' : '0');
  formData.append('vehicle_weight_category', data.vehicle_weight_category);

  // הוספת מספר כריות למסלול 2 (3.5-4.5 טון)
  if (data.vehicle_weight_category === 'between_3500_4500' && data.airbags_count !== undefined) {
    formData.append('airbags_count', data.airbags_count.toString());
  }
  // הוספת ציון ירוק אם קיים
  if (data.green_rating_group !== undefined && data.green_rating_group !== null) {
    formData.append('green_rating_group', data.green_rating_group.toString());
  }

  // הוספת אבזור בטיחותי רק למתחת ל-3.5 טון
  if (data.vehicle_weight_category === 'under_3500' && data.safety_features) {
    formData.append('safety_features', JSON.stringify(data.safety_features));
  }

  try {
    // console.log('🌐 Making request to:', `${API_BASE_URL}/Api/service/enduser/get_basic_calculation_m1_commercial`);

    const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_basic_calculation_m1_commercial`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // console.log('📥 תשובה מהשרת למחשבון M1 מסחרי:', result);

    return result;
  } catch (error) {
    console.error('❌ שגיאה במחשבון M1 מסחרי:', error);
    return {
      res: 'NOK',
      msg: 'שגיאה בתקשורת עם השרת',
      error_code: 'NETWORK_ERROR'
    };
  }
};

/**
 * חישוב M1 מסחרי - שלב 2 סופי (עם מחיר לצרכן ומס יוקרה)
 * רק למסלול 3 (רכבים מתחת ל-3.5 טון)
 */
export const calculateM1CommercialFinal = async (
  data: {
    manufacturing_date: string;
    vehicle_price_foreign: number;
    currency_type: string;
    exchange_rate: number;
    shipping_cost_foreign: number;
    insurance_cost_foreign: number;
    insurance_currency_type: string;
    insurance_exchange_rate: number;
    local_expenses_ils: number;
    has_origin_declaration: boolean;
    green_rating_group?: number;
    vehicle_weight_category: string;
    safety_features?: {
      airbagsCount: number;
      systems: {
        blindSpotDetection: boolean;
        blindSpotCollisionPrevention: boolean;
        reverseBraking: boolean;
        driverDistraction: boolean;
        childAlert: boolean;
        doorOpenWarning: boolean;
        adaptiveLighting: boolean;
        junctionEmergencyBraking: boolean;
      };
    };
    consumer_price: number;
    calculated_price_with_vat: number;
  }
): Promise<ApiResponse<any>> => {
  // console.log('🚗🏢 שליחת נתונים למחשבון M1 מסחרי (שלב 2 - סופי):', {
  //   consumer_price: data.consumer_price,
  //   calculated_price_with_vat: data.calculated_price_with_vat
  // });

  // Get session token from cookies
  const getSessionToken = (): string | null => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'enduser_session_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  const sessionToken = getSessionToken();
  if (!sessionToken) {
    return {
      res: 'NOK',
      msg: 'לא נמצא token של המשתמש',
      error_code: 'NO_SESSION_TOKEN'
    };
  }

  const formData = new FormData();
  formData.append('session_token', sessionToken);

  // המרת כל השדות ל-FormData
  formData.append('manufacturing_date', data.manufacturing_date);
  formData.append('vehicle_price_foreign', data.vehicle_price_foreign.toString());
  formData.append('currency_type', data.currency_type);
  formData.append('exchange_rate', data.exchange_rate.toString());
  formData.append('shipping_cost_foreign', data.shipping_cost_foreign.toString());
  formData.append('insurance_cost_foreign', data.insurance_cost_foreign.toString());
  formData.append('insurance_currency_type', data.insurance_currency_type || data.currency_type);
  formData.append('insurance_exchange_rate', data.insurance_exchange_rate.toString());
  formData.append('local_expenses_ils', data.local_expenses_ils.toString());
  formData.append('has_origin_declaration', data.has_origin_declaration ? '1' : '0');
  formData.append('vehicle_weight_category', data.vehicle_weight_category);
  formData.append('consumer_price', data.consumer_price.toString());
  formData.append('calculated_price_with_vat', data.calculated_price_with_vat.toString());

  // הוספת ציון ירוק אם קיים
  if (data.green_rating_group !== undefined && data.green_rating_group !== null) {
    formData.append('green_rating_group', data.green_rating_group.toString());
  }

  // הוספת אבזור בטיחותי
  if (data.safety_features) {
    formData.append('safety_features', JSON.stringify(data.safety_features));
  }

  try {
    // console.log('🌐 Making request to:', `${API_BASE_URL}/Api/service/enduser/get_final_calculation_m1_commercial`);

    const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_final_calculation_m1_commercial`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // console.log('📥 תשובה מהשרת למחשבון M1 מסחרי סופי:', result);
    notifyFinalCalculationCompleted('/Api/service/enduser/get_final_calculation_m1_commercial', result);

    return result;
  } catch (error) {
    console.error('❌ שגיאה במחשבון M1 מסחרי סופי:', error);
    return {
      res: 'NOK',
      msg: 'שגיאה בתקשורת עם השרת',
      error_code: 'NETWORK_ERROR'
    };
  }
};

/**
 * חישוב N1 & N2 בנזין/דיזל/היברידי - ייבוא מסחרי (שלב 1)
 * זהה למחשבון M1 מסחרי עם שינוי אחד: מינימום 4 כריות במקום 6
 * 
 * 3 מסלולים לפי משקל:
 * 1. מעל 4.5 טון: רק מכס + מע"מ
 * 2. 3.5-4.5 טון: מס קניה 72%
 * 3. מתחת ל-3.5 טון: מס קניה 83% + ציון ירוק + אבזור M1 (8 מערכות חדשות)
 */
const calculateN1N2Commercial = async (
  data: {
    manufacturing_date: string;
    vehicle_price_foreign: number;
    currency_type: string;
    exchange_rate: number;
    shipping_cost_foreign: number;
    insurance_cost_foreign: number;
    insurance_currency_type: string;
    insurance_exchange_rate: number;
    local_expenses_ils: number;
    has_origin_declaration: boolean;
    green_rating_group?: number;
    vehicle_weight_category: 'under_3500' | 'between_3500_4500' | 'over_4500';
    airbags_count?: number; // מספר כריות למסלול 2 (3.5-4.5 טון)
    safety_features?: {
      airbagsCount: number;
      systems: {
        blindSpotDetection: boolean;           // זיהוי שטח מת
        blindSpotCollisionPrevention: boolean; // מניעת התנגשות צד
        reverseBraking: boolean;               // בלימה לאחור
        driverDistraction: boolean;            // התרעה היסח דעת
        childAlert: boolean;                   // התרעה ילדים
        doorOpenWarning: boolean;              // אזהרת דלת
        adaptiveLighting: boolean;             // תאורה אדפטיבית
        junctionEmergencyBraking: boolean;     // בלימת חירום בצמתים
      };
    };
  }
): Promise<ApiResponse<any>> => {
  // console.log('🚚🏢 שליחת נתונים למחשבון N1N2 מסחרי (שלב 1):', data);

  // Get session token from cookies
  const getSessionToken = (): string | null => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'enduser_session_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  const sessionToken = getSessionToken();
  if (!sessionToken) {
    return {
      res: 'NOK',
      msg: 'לא נמצא token של המשתמש',
      error_code: 'NO_SESSION_TOKEN'
    };
  }

  const formData = new FormData();
  formData.append('session_token', sessionToken);

  // המרת כל השדות ל-FormData
  formData.append('manufacturing_date', data.manufacturing_date);
  formData.append('vehicle_price_foreign', data.vehicle_price_foreign.toString());
  formData.append('currency_type', data.currency_type);
  formData.append('exchange_rate', data.exchange_rate.toString());
  formData.append('shipping_cost_foreign', data.shipping_cost_foreign.toString());
  formData.append('insurance_cost_foreign', data.insurance_cost_foreign.toString());
  formData.append('insurance_currency_type', data.insurance_currency_type || data.currency_type);
  formData.append('insurance_exchange_rate', data.insurance_exchange_rate.toString());
  formData.append('local_expenses_ils', data.local_expenses_ils.toString());
  formData.append('has_origin_declaration', data.has_origin_declaration ? '1' : '0');
  formData.append('vehicle_weight_category', data.vehicle_weight_category);

  // הוספת מספר כריות למסלול 2 (3.5-4.5 טון)
  if (data.vehicle_weight_category === 'between_3500_4500' && data.airbags_count !== undefined) {
    formData.append('airbags_count', data.airbags_count.toString());
  }

  // הוספת ציון ירוק אם קיים
  if (data.green_rating_group !== undefined && data.green_rating_group !== null) {
    formData.append('green_rating_group', data.green_rating_group.toString());
  }

  // הוספת אבזור בטיחותי רק למתחת ל-3.5 טון
  if (data.vehicle_weight_category === 'under_3500' && data.safety_features) {
    formData.append('safety_features', JSON.stringify(data.safety_features));
  }

  try {
    // console.log('🌐 Making request to:', `${API_BASE_URL}/Api/service/enduser/get_basic_calculation_n1n2_commercial`);

    const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_basic_calculation_n1n2_commercial`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // console.log('📥 תשובה מהשרת למחשבון N1N2 מסחרי:', result);

    return result;
  } catch (error) {
    console.error('❌ שגיאה במחשבון N1N2 מסחרי:', error);
    return {
      res: 'NOK',
      msg: 'שגיאה בתקשורת עם השרת',
      error_code: 'NETWORK_ERROR'
    };
  }
};

/**
 * חישוב N1N2 מסחרי - שלב 2 סופי (עם מחיר לצרכן ומס יוקרה)
 * רק למסלול 3 (רכבים מתחת ל-3.5 טון)
 */
export const calculateN1N2CommercialFinal = async (
  data: {
    manufacturing_date: string;
    vehicle_price_foreign: number;
    currency_type: string;
    exchange_rate: number;
    shipping_cost_foreign: number;
    insurance_cost_foreign: number;
    insurance_currency_type: string;
    insurance_exchange_rate: number;
    local_expenses_ils: number;
    has_origin_declaration: boolean;
    green_rating_group?: number;
    vehicle_weight_category: string;
    safety_features?: {
      airbagsCount: number;
      systems: {
        blindSpotDetection: boolean;
        blindSpotCollisionPrevention: boolean;
        reverseBraking: boolean;
        driverDistraction: boolean;
        childAlert: boolean;
        doorOpenWarning: boolean;
        adaptiveLighting: boolean;
        junctionEmergencyBraking: boolean;
      };
    };
    consumer_price: number;
    calculated_price_with_vat: number;
  }
): Promise<ApiResponse<any>> => {
  
  // Get session token from cookies
  const getSessionToken = (): string | null => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'enduser_session_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  const sessionToken = getSessionToken();
  if (!sessionToken) {
    return {
      res: 'NOK',
      msg: 'לא נמצא token של המשתמש',
      error_code: 'NO_SESSION_TOKEN'
    };
  }

  const formData = new FormData();
  formData.append('session_token', sessionToken);

  // המרת כל השדות ל-FormData
  formData.append('manufacturing_date', data.manufacturing_date);
  formData.append('vehicle_price_foreign', data.vehicle_price_foreign.toString());
  formData.append('currency_type', data.currency_type);
  formData.append('exchange_rate', data.exchange_rate.toString());
  formData.append('shipping_cost_foreign', data.shipping_cost_foreign.toString());
  formData.append('insurance_cost_foreign', data.insurance_cost_foreign.toString());
  formData.append('insurance_currency_type', data.insurance_currency_type || data.currency_type);
  formData.append('insurance_exchange_rate', data.insurance_exchange_rate.toString());
  formData.append('local_expenses_ils', data.local_expenses_ils.toString());
  formData.append('has_origin_declaration', data.has_origin_declaration ? '1' : '0');
  formData.append('vehicle_weight_category', data.vehicle_weight_category);
  formData.append('consumer_price', data.consumer_price.toString());
  formData.append('calculated_price_with_vat', data.calculated_price_with_vat.toString());

  // הוספת ציון ירוק אם קיים
  if (data.green_rating_group !== undefined && data.green_rating_group !== null) {
    formData.append('green_rating_group', data.green_rating_group.toString());
  }

  // הוספת אבזור בטיחותי
  if (data.safety_features) {
    formData.append('safety_features', JSON.stringify(data.safety_features));
  }

  try {
    // console.log('🌐 Making request to:', `${API_BASE_URL}/Api/service/enduser/get_final_calculation_n1n2_commercial`);

    const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_final_calculation_n1n2_commercial`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // console.log('📥 תשובה מהשרת למחשבון N1N2 מסחרי סופי:', result);
    notifyFinalCalculationCompleted('/Api/service/enduser/get_final_calculation_n1n2_commercial', result);

    return result;
  } catch (error) {
    console.error('❌ שגיאה במחשבון N1N2 מסחרי סופי:', error);
    return {
      res: 'NOK',
      msg: 'שגיאה בתקשורת עם השרת',
      error_code: 'NETWORK_ERROR'
    };
  }
};



/**
 * חישוב M1 חשמלי - ייבוא מסחרי (שלב 1)
 * 3 מסלולים לפי משקל:
 * 1. מעל 4.5 טון: רק מכס + מע"מ
 * 2. 3.5-4.5 טון: בחירת MAX בין 72% עם הנחות לבין 45%
 * 3. מתחת ל-3.5 טון: בחירת MAX בין 83% עם הנחות לבין 45% + מס יוקרה
 * 
 * ציון ירוק: דיפולטיבי - תמיד group_number = 1 (לא צריך לשלוח)
 */
export const calculateM1ElectricCommercial = async (
  data: {
    manufacturing_date: string;
    vehicle_price_foreign: number;
    currency_type: string;
    exchange_rate: number;
    shipping_cost_foreign: number;
    insurance_cost_foreign: number;
    insurance_currency_type: string;
    insurance_exchange_rate: number;
    local_expenses_ils: number;
    has_origin_declaration: boolean;
    vehicle_weight_category: 'under_3500' | 'between_3500_4500' | 'over_4500';
    airbags_count?: number; // מספר כריות למסלול 2 (3.5-4.5 טון)
    safety_features?: {
      airbagsCount: number;
      systems: {
        blindSpotDetection: boolean;           // זיהוי כלי רכב ב"שטח מת"
        blindSpotCollisionPrevention: boolean; // אקטיבית למניעת התנגשות צד
        reverseBraking: boolean;               // בלימה אוטומטית לאחור
        driverDistraction: boolean;            // התרעה היסח דעת הנהג
        childAlert: boolean;                   // התרעה ילדים ברכב
        doorOpenWarning: boolean;              // אזהרת פתיחת דלת
        adaptiveLighting: boolean;             // תאורה אדפטיבית
        junctionEmergencyBraking: boolean;     // בלימת חירום בצמתים
      };
    };
  }
): Promise<ApiResponse<any>> => {
  // console.log('⚡🚗🏢 שליחת נתונים למחשבון M1 חשמלי מסחרי (שלב 1):', data);

  // Get session token from cookies
  const getSessionToken = (): string | null => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'enduser_session_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  const sessionToken = getSessionToken();
  if (!sessionToken) {
    return {
      res: 'NOK',
      msg: 'לא נמצא token של המשתמש',
      error_code: 'NO_SESSION_TOKEN'
    };
  }

  const formData = new FormData();
  formData.append('session_token', sessionToken);

  // המרת כל השדות ל-FormData
  formData.append('manufacturing_date', data.manufacturing_date);
  formData.append('vehicle_price_foreign', data.vehicle_price_foreign.toString());
  formData.append('currency_type', data.currency_type);
  formData.append('exchange_rate', data.exchange_rate.toString());
  formData.append('shipping_cost_foreign', data.shipping_cost_foreign.toString());
  formData.append('insurance_cost_foreign', data.insurance_cost_foreign.toString());
  formData.append('insurance_currency_type', data.insurance_currency_type || data.currency_type);
  formData.append('insurance_exchange_rate', data.insurance_exchange_rate.toString());
  formData.append('local_expenses_ils', data.local_expenses_ils.toString());
  formData.append('has_origin_declaration', data.has_origin_declaration ? '1' : '0');
  formData.append('vehicle_weight_category', data.vehicle_weight_category);

  // הוספת מספר כריות למסלול 2 (3.5-4.5 טון)
  if (data.vehicle_weight_category === 'between_3500_4500' && data.airbags_count !== undefined) {
    formData.append('airbags_count', data.airbags_count.toString());
  }

  // הוספת אבזור בטיחותי רק למתחת ל-3.5 טון
  if (data.vehicle_weight_category === 'under_3500' && data.safety_features) {
    formData.append('safety_features', JSON.stringify(data.safety_features));
  }

  try {
    // console.log('🌐 Making request to:', `${API_BASE_URL}/Api/service/enduser/get_basic_calculation_m1_electric_commercial`);

    const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_basic_calculation_m1_electric_commercial`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // console.log('📥 תשובה מהשרת למחשבון M1 חשמלי מסחרי:', result);

    return result;
  } catch (error) {
    console.error('❌ שגיאה במחשבון M1 חשמלי מסחרי:', error);
    return {
      res: 'NOK',
      msg: 'שגיאה בתקשורת עם השרת',
      error_code: 'NETWORK_ERROR'
    };
  }
};

/**
 * חישוב M1 חשמלי מסחרי - שלב 2 סופי (עם מחיר לצרכן ומס יוקרה)
 * רק למסלול 3 (רכבים מתחת ל-3.5 טון)
 */
export const calculateM1ElectricCommercialFinal = async (
  data: {
    manufacturing_date: string;
    vehicle_price_foreign: number;
    currency_type: string;
    exchange_rate: number;
    shipping_cost_foreign: number;
    insurance_cost_foreign: number;
    insurance_currency_type: string;
    insurance_exchange_rate: number;
    local_expenses_ils: number;
    has_origin_declaration: boolean;
    vehicle_weight_category: string;
    safety_features?: {
      airbagsCount: number;
      systems: {
        blindSpotDetection: boolean;
        blindSpotCollisionPrevention: boolean;
        reverseBraking: boolean;
        driverDistraction: boolean;
        childAlert: boolean;
        doorOpenWarning: boolean;
        adaptiveLighting: boolean;
        junctionEmergencyBraking: boolean;
      };
    };
    consumer_price: number;
    calculated_price_with_vat: number;
  }
): Promise<ApiResponse<any>> => {
  

  // Get session token from cookies
  const getSessionToken = (): string | null => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'enduser_session_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  const sessionToken = getSessionToken();
  if (!sessionToken) {
    return {
      res: 'NOK',
      msg: 'לא נמצא token של המשתמש',
      error_code: 'NO_SESSION_TOKEN'
    };
  }

  const formData = new FormData();
  formData.append('session_token', sessionToken);

  // המרת כל השדות ל-FormData
  formData.append('manufacturing_date', data.manufacturing_date);
  formData.append('vehicle_price_foreign', data.vehicle_price_foreign.toString());
  formData.append('currency_type', data.currency_type);
  formData.append('exchange_rate', data.exchange_rate.toString());
  formData.append('shipping_cost_foreign', data.shipping_cost_foreign.toString());
  formData.append('insurance_cost_foreign', data.insurance_cost_foreign.toString());
  formData.append('insurance_currency_type', data.insurance_currency_type || data.currency_type);
  formData.append('insurance_exchange_rate', data.insurance_exchange_rate.toString());
  formData.append('local_expenses_ils', data.local_expenses_ils.toString());
  formData.append('has_origin_declaration', data.has_origin_declaration ? '1' : '0');
  formData.append('vehicle_weight_category', data.vehicle_weight_category);
  formData.append('consumer_price', data.consumer_price.toString());
  formData.append('calculated_price_with_vat', data.calculated_price_with_vat.toString());

  // הוספת אבזור בטיחותי
  if (data.safety_features) {
    formData.append('safety_features', JSON.stringify(data.safety_features));
  }

  try {
    // console.log('🌐 Making request to:', `${API_BASE_URL}/Api/service/enduser/get_final_calculation_m1_electric_commercial`);

    const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_final_calculation_m1_electric_commercial`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // console.log('📥 תשובה מהשרת למחשבון M1 חשמלי מסחרי (סופי):', result);
    notifyFinalCalculationCompleted('/Api/service/enduser/get_final_calculation_m1_electric_commercial', result);

    return result;
  } catch (error) {
    console.error('❌ שגיאה במחשבון M1 חשמלי מסחרי (סופי):', error);
    return {
      res: 'NOK',
      msg: 'שגיאה בתקשורת עם השרת',
      error_code: 'NETWORK_ERROR'
    };
  }
};
/**
 * חישוב N1 & N2 חשמלי - ייבוא מסחרי (שלב 1)
 * 3 מסלולים לפי משקל:
 * 1. מעל 4.5 טון: רק מכס + מע"מ
 * 2. 3.5-4.5 טון: בחירת MAX בין 72% עם הנחות לבין 35%
 * 3. מתחת ל-3.5 טון: בחירת MAX בין 83% עם הנחות לבין 35% + מס יוקרה
 * 
 * ציון ירוק: דיפולטיבי - תמיד group_number = 1 (לא צריך לשלוח)
 */
export const calculateN1N2ElectricCommercial = async (
  data: {
    manufacturing_date: string;
    vehicle_price_foreign: number;
    currency_type: string;
    exchange_rate: number;
    shipping_cost_foreign: number;
    insurance_cost_foreign: number;
    insurance_currency_type: string;
    insurance_exchange_rate: number;
    local_expenses_ils: number;
    has_origin_declaration: boolean;
    vehicle_weight_category: 'under_3500' | 'between_3500_4500' | 'over_4500';
    airbags_count?: number; // מספר כריות למסלול 2 (3.5-4.5 טון)
    safety_features?: {
      airbagsCount: number;
      systems: {
        blindSpotDetection: boolean;           // זיהוי כלי רכב ב"שטח מת"
        blindSpotCollisionPrevention: boolean; // אקטיבית למניעת התנגשות צד
        reverseBraking: boolean;               // בלימה אוטומטית לאחור
        driverDistraction: boolean;            // התרעה היסח דעת הנהג
        childAlert: boolean;                   // התרעה ילדים ברכב
        doorOpenWarning: boolean;              // אזהרת פתיחת דלת
        adaptiveLighting: boolean;             // תאורה אדפטיבית
        junctionEmergencyBraking: boolean;     // בלימת חירום בצמתים
      };
    };
  }
): Promise<ApiResponse<any>> => {
  // console.log('⚡🚛🏢 שליחת נתונים למחשבון N1 & N2 חשמלי מסחרי (שלב 1):', data);

  // Get session token from cookies
  const getSessionToken = (): string | null => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'enduser_session_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  const sessionToken = getSessionToken();
  if (!sessionToken) {
    return {
      res: 'NOK',
      msg: 'לא נמצא token של המשתמש',
      error_code: 'NO_SESSION_TOKEN'
    };
  }

  const formData = new FormData();
  formData.append('session_token', sessionToken);

  // המרת כל השדות ל-FormData
  formData.append('manufacturing_date', data.manufacturing_date);
  formData.append('vehicle_price_foreign', data.vehicle_price_foreign.toString());
  formData.append('currency_type', data.currency_type);
  formData.append('exchange_rate', data.exchange_rate.toString());
  formData.append('shipping_cost_foreign', data.shipping_cost_foreign.toString());
  formData.append('insurance_cost_foreign', data.insurance_cost_foreign.toString());
  formData.append('insurance_currency_type', data.insurance_currency_type || data.currency_type);
  formData.append('insurance_exchange_rate', data.insurance_exchange_rate.toString());
  formData.append('local_expenses_ils', data.local_expenses_ils.toString());
  formData.append('has_origin_declaration', data.has_origin_declaration ? '1' : '0');
  formData.append('vehicle_weight_category', data.vehicle_weight_category);

  // הוספת מספר כריות למסלול 2 (3.5-4.5 טון)
  if (data.vehicle_weight_category === 'between_3500_4500' && data.airbags_count !== undefined) {
    formData.append('airbags_count', data.airbags_count.toString());
  }

  // הוספת אבזור בטיחותי רק למתחת ל-3.5 טון
  if (data.vehicle_weight_category === 'under_3500' && data.safety_features) {
    formData.append('safety_features', JSON.stringify(data.safety_features));
  }

  try {
    // console.log('🌐 Making request to:', `${API_BASE_URL}/Api/service/enduser/get_basic_calculation_n1_n2_electric_commercial`);

    const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_basic_calculation_n1_n2_electric_commercial`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // console.log('📥 תשובה מהשרת למחשבון N1 & N2 חשמלי מסחרי:', result);

    return result;
  } catch (error) {
    console.error('❌ שגיאה במחשבון N1 & N2 חשמלי מסחרי:', error);
    return {
      res: 'NOK',
      msg: 'שגיאה בתקשורת עם השרת',
      error_code: 'NETWORK_ERROR'
    };
  }
};

/**
 * חישוב N1 & N2 חשמלי מסחרי - שלב 2 סופי (עם מחיר לצרכן ומס יוקרה)
 * רק למסלול 3 (רכבים מתחת ל-3.5 טון)
 */
export const calculateN1N2ElectricCommercialFinal = async (
  data: {
    manufacturing_date: string;
    vehicle_price_foreign: number;
    currency_type: string;
    exchange_rate: number;
    shipping_cost_foreign: number;
    insurance_cost_foreign: number;
    insurance_currency_type: string;
    insurance_exchange_rate: number;
    local_expenses_ils: number;
    has_origin_declaration: boolean;
    vehicle_weight_category: string;
    safety_features?: {
      airbagsCount: number;
      systems: {
        blindSpotDetection: boolean;
        blindSpotCollisionPrevention: boolean;
        reverseBraking: boolean;
        driverDistraction: boolean;
        childAlert: boolean;
        doorOpenWarning: boolean;
        adaptiveLighting: boolean;
        junctionEmergencyBraking: boolean;
      };
    };
    consumer_price: number;
    calculated_price_with_vat: number;
  }
): Promise<ApiResponse<any>> => {
  // console.log('⚡🚛🏢 שליחת נתונים למחשבון N1 & N2 חשמלי מסחרי (שלב 2 - סופי):', {
    // consumer_price: data.consumer_price,
    // calculated_price_with_vat: data.calculated_price_with_vat
  // });

  // Get session token from cookies
  const getSessionToken = (): string | null => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'enduser_session_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  const sessionToken = getSessionToken();
  if (!sessionToken) {
    return {
      res: 'NOK',
      msg: 'לא נמצא token של המשתמש',
      error_code: 'NO_SESSION_TOKEN'
    };
  }

  const formData = new FormData();
  formData.append('session_token', sessionToken);

  // המרת כל השדות ל-FormData
  formData.append('manufacturing_date', data.manufacturing_date);
  formData.append('vehicle_price_foreign', data.vehicle_price_foreign.toString());
  formData.append('currency_type', data.currency_type);
  formData.append('exchange_rate', data.exchange_rate.toString());
  formData.append('shipping_cost_foreign', data.shipping_cost_foreign.toString());
  formData.append('insurance_cost_foreign', data.insurance_cost_foreign.toString());
  formData.append('insurance_currency_type', data.insurance_currency_type || data.currency_type);
  formData.append('insurance_exchange_rate', data.insurance_exchange_rate.toString());
  formData.append('local_expenses_ils', data.local_expenses_ils.toString());
  formData.append('has_origin_declaration', data.has_origin_declaration ? '1' : '0');
  formData.append('vehicle_weight_category', data.vehicle_weight_category);
  formData.append('consumer_price', data.consumer_price.toString());
  formData.append('calculated_price_with_vat', data.calculated_price_with_vat.toString());

  // הוספת אבזור בטיחותי
  if (data.safety_features) {
    formData.append('safety_features', JSON.stringify(data.safety_features));
  }

  try {
    // console.log('🌐 Making request to:', `${API_BASE_URL}/Api/service/enduser/get_final_calculation_n1_n2_electric_commercial`);

    const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_final_calculation_n1_n2_electric_commercial`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // console.log('📥 תשובה מהשרת למחשבון N1 & N2 חשמלי מסחרי (סופי):', result);
    notifyFinalCalculationCompleted('/Api/service/enduser/get_final_calculation_n1_n2_electric_commercial', result);

    return result;
  } catch (error) {
    console.error('❌ שגיאה במחשבון N1 & N2 חשמלי מסחרי (סופי):', error);
    return {
      res: 'NOK',
      msg: 'שגיאה בתקשורת עם השרת',
      error_code: 'NETWORK_ERROR'
    };
  }
};


/**
 * שליפת היסטוריית חישובים של המשתמש
 * @param offset - התחלה (0 = הכי חדש)
 * @param limit - כמות לשליפה (מקסימום 50)
 * @param calculationType - סוג מחשבון לסינון (אופציונלי)
 */
export const getCalculationHistory = async (
  offset: number = 0,
  limit: number = 50,
  calculationType?: string
): Promise<any> => {
  const formData = new FormData();
  const getSessionToken = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'enduser_session_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  const sessionToken = getSessionToken();
  if (!sessionToken) {
    throw new Error('אין הרשאה - יש להתחבר מחדש');
  }

  formData.append('session_token', sessionToken);
  formData.append('offset', offset.toString());
  formData.append('limit', limit.toString());

  if (calculationType) {
    formData.append('calculation_type', calculationType);
  }

  const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_calculation_history`, {
    method: 'POST',
    body: formData,
  });

  const result = await response.json();
  return result;
};

/**
 * מחיקה רכה של חישוב
 * @param calculationId - מזהה החישוב למחיקה
 */
export const deleteCalculation = async (calculationId: number): Promise<any> => {
  const formData = new FormData();
  const getSessionToken = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'enduser_session_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  const sessionToken = getSessionToken();

  if (!sessionToken) {
    throw new Error('אין הרשאה - יש להתחבר מחדש');
  }

  formData.append('session_token', sessionToken);
  formData.append('calculation_id', calculationId.toString());

  const response = await fetch(`${API_BASE_URL}/Api/service/enduser/delete_calculation`, {
    method: 'POST',
    body: formData,
  });

  const result = await response.json();
  return result;
};
/**
 * שליפת חישוב בודד לצפייה
 * @param calculationId - מזהה החישוב
 */
export const getSingleCalculation = async (calculationId: number): Promise<any> => {
  const formData = new FormData();

  const getSessionToken = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'enduser_session_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  const sessionToken = getSessionToken();

  if (!sessionToken) {
    throw new Error('אין הרשאה - יש להתחבר מחדש');
  }

  formData.append('session_token', sessionToken);
  formData.append('calculation_id', calculationId.toString());

  const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_single_calculation`, {
    method: 'POST',
    body: formData,
  });

  const result = await response.json();
  return result;
};
export const calculatorService = new CalculatorService();
// Export of individual functions for easier import
export const getCalculatorData = () => calculatorService.getCalculatorData();
export const getBasicCalculationM1 = (input: CalculationInput) => calculatorService.getBasicCalculationM1(input);
export const getFinalCalculationM1 = (input: CalculationInput, chosenConsumerPrice: number) => calculatorService.getFinalCalculationM1(input, chosenConsumerPrice);
export const getTrailerCalculation = (input: TrailerCalculationRequest) => calculateTrailer(input);
export const getTaxiCalculation = (input: TaxiCalculationRequest) => calculateTaxi(input);
export const getJetSkiCalculation = (input: JetSkiCalculationRequest) => calculateJetSki(input);
export const saveCalculationWithNotes = (data: SaveCalculationRequest) => saveCalculation(data);
export const getMotorcycleCalculation = (input: MotorcycleCalculationRequest) => calculateMotorcycle(input);
export const getBasicCalculationM1Electric = (input: M1ElectricCalculationInput) =>
  calculatorService.getBasicCalculationM1Electric(input);
export const getFinalCalculationM1Electric = (input: M1ElectricCalculationInput, chosenConsumerPrice: number) =>
  calculatorService.getFinalCalculationM1Electric(input, chosenConsumerPrice);
export const getBasicCalculationN1Electric = (input: N1ElectricCalculationInput) =>
  calculatorService.getBasicCalculationN1Electric(input);

export const getFinalCalculationN1Electric = (input: N1ElectricCalculationInput, chosenConsumerPrice: number) =>
  calculatorService.getFinalCalculationN1Electric(input, chosenConsumerPrice);
export const getMotorcycleCommercialCalculation = (input: MotorcycleCommercialCalculationRequest) =>
  calculateMotorcycleCommercial(input);
export const getJetSkiCommercialCalculation = (input: JetSkiCommercialCalculationRequest) =>
  calculateJetSkiCommercial(input);
export const getTaxiCommercialCalculation = (input: TaxiCommercialCalculationRequest) =>
  calculateTaxiCommercial(input);
export const getM1CommercialCalculation = (input: any) => calculateM1Commercial(input);
export const getM1CommercialFinalCalculation = (input: any) => calculateM1CommercialFinal(input);
export const getN1N2CommercialCalculation = (input: any) => calculateN1N2Commercial(input);
export const getN1N2CommercialFinalCalculation = (input: any) => calculateN1N2CommercialFinal(input);
export const getM1ElectricCommercialCalculation = (input: any) => calculateM1ElectricCommercial(input);
export const getM1ElectricCommercialFinalCalculation = (input: any) => calculateM1ElectricCommercialFinal(input);
export const getN1N2ElectricCommercialCalculation = (input: any) => calculateN1N2ElectricCommercial(input);
export const getN1N2ElectricCommercialFinalCalculation = (input: any) => calculateN1N2ElectricCommercialFinal(input);